// src/components/Login.jsx
import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase.config';
import { useAuth } from '../../contexts/AuthContext';
import { PORTAL_ACCESS, ACCESS_ERROR_MESSAGE, messageForAccess } from '../../contexts/portalAccess';

const friendlyAuthError = (error) => {
  switch (error && error.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Wrong email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/user-disabled':
      return 'This account is disabled.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      console.warn('Sign-in failed:', error && error.code);
      return 'Sign-in failed. Please try again.';
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  // submitting: from the click until this page is done with the attempt.
  // awaitingAccess: the sign-in call itself has RESOLVED and we are now
  // waiting for AuthContext to resolve the new session's role - only then may
  // an ERROR result drop the session (never while the sign-in is in flight).
  const [submitting, setSubmitting] = useState(false);
  const [awaitingAccess, setAwaitingAccess] = useState(false);
  const navigate = useNavigate();
  const { user, access, accessChecked, isAdmin, deniedReason, clearDenied } = useAuth();

  // AuthContext owns the role check (one Firestore read per session) and
  // signs non-admins out; this page only signs in and reacts:
  //  - admin           -> /admin (also covers an admin who simply opens /login)
  //  - pending/denied  -> the context already signed out; deniedReason below
  //                       explains why instead of a silent bounce
  //  - error           -> drop the session so the next attempt re-runs the check
  useEffect(() => {
    if (!user || !accessChecked) return;
    if (isAdmin) {
      navigate('/admin');
      return;
    }
    if (access === PORTAL_ACCESS.ERROR && awaitingAccess) {
      signOut(auth).catch(() => {});
      setError(ACCESS_ERROR_MESSAGE);
      setSubmitting(false);
      setAwaitingAccess(false);
    }
  }, [user, access, accessChecked, isAdmin, awaitingAccess, navigate]);

  useEffect(() => {
    if (deniedReason) {
      setError(messageForAccess(deniedReason));
      setSubmitting(false);
      setAwaitingAccess(false);
      clearDenied();
    }
  }, [deniedReason, clearDenied]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Firebase notifies auth listeners only on a uid CHANGE. Re-signing in
      // the account that is already signed in (e.g. after an ERROR on a cold
      // load) would fire nothing and leave the stale result in place, so
      // always start the attempt from a signed-out state.
      if (auth.currentUser) {
        await signOut(auth).catch(() => {});
      }
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // Navigation happens in the effect above once the role is known.
      setAwaitingAccess(true);
    } catch (err) {
      setError(friendlyAuthError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(160deg,#eef0f3,#c6cdd6)] p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-xl border border-[rgba(150,165,180,.25)] bg-white shadow-[0_1px_3px_rgba(29,43,51,.08)]">
        <div className="bg-[linear-gradient(120deg,#4a5c70,#374a5e_55%,#2a3a4c)] px-6 py-5 text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#9ecbf7]">Admin Portal</p>
          <h1 className="mt-1 text-xl font-bold text-white">Simplicity Dynamics</h1>
        </div>
        <div className="p-6">
          {error && <p className="mb-4 text-sm text-[#d96a5b]">{error}</p>}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-[#2f66b8] p-2 text-white hover:bg-[#285aa5] disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <p className="mt-4 text-sm text-[#5b6d7a]">
            This login is for Simplicity Dynamics administrators. SimpleOffice users sign in at{' '}
            <a href="https://simplicity-office.web.app" className="underline">simplicity-office.web.app</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
